import {
  Box,
  Button,
  capitalize,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AthleteSelect } from '../../../components/layout/athlete-select';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import { useViewer } from '../../../context/viewer';
import { jotaiStore } from '../../../utils/jotai';
import { AthleteSheet } from '../athlete-sheet';
import { BlockSheet } from '../block-sheet';
import { recentSwapsAtom } from '../components/apply-swap-drawer';
import { useBlockExercises } from '../hooks/useBlockExercises';
import { BaseBlockSheetProps, BlockGroupBy, BlockView } from '../types';

type BlockBuilderProps = {
  groupBy: BlockGroupBy;
  sessionId: number;
  cycleId: number;
  athletes: AthletePreview[];
  selectedAthleteId: string | null;
  useSingleAthleteMode: boolean;
  athleteTaskMap: Record<string, boolean>;
  setSessionId: (sessionId: number) => void;
  setCycleId: (cycleId: number) => void;
  setSelectedAthleteId: (athleteId: string | null) => void;
  setGroupBy: (groupBy: BlockGroupBy) => void;
  onBlockLayoutChange: (cycleSortOrder: number, sessions: number) => void;
  selectedWorkoutId?: string;
  initShowActuals?: boolean;
} & Omit<BaseBlockSheetProps, 'view'>;

export const BlockBuilder = ({
  groupBy,
  sessionId,
  cycleId,
  athletes,
  selectedAthleteId,
  useSingleAthleteMode,
  athleteTaskMap,
  setSessionId,
  setCycleId,
  setSelectedAthleteId,
  setGroupBy,
  onBlockLayoutChange,
  selectedWorkoutId,
  initShowActuals = false,
  ...props
}: BlockBuilderProps) => {
  const {
    palette: { info },
  } = useTheme();
  const { t } = useTranslation();
  // Actuals Log gates the Show-actuals toggle: without it actuals writes
  // are denied server-side, so the toggle gets disabled with a reason
  // instead of being removed (SC-4378).
  const { hasPermissions } = useViewer();
  const canEditActuals = hasPermissions('actuals', 'edit');
  // plan.blocks.viewActuals gates the same toggle (SC-4811): a coach who
  // may not see actuals inside the Block Builder cannot turn the layer on.
  const canViewActuals = hasPermissions('plan.blocks', 'viewActuals');
  // Blocks Edit gates the builder's editing UI (SC-4767): revoked coaches
  // get the read-only sheets — the same mechanism cross-team viewers get.
  // Server-side, the draft saves are governed by the same key (SC-4766);
  // this check alone is not sufficient.
  const canEditBlocks = hasPermissions('plan.blocks', 'edit');
  // Cross-team blocks arrive read-only from the page. Blocks Edit can add
  // more read-only, never take it away.
  const isReadOnly = props.readOnly || !canEditBlocks;

  // Start fetching the exercise library in the background so the exercise
  // pickers can filter locally instead of querying per keystroke. Keyed off
  // the block's team: on refresh, selectedTeam is only corrected to it in an
  // effect after mount.
  useBlockExercises(props.block.teamId);

  const selectedAthletePreview = useMemo(
    () => athletes.find((a) => a.id === selectedAthleteId) ?? null,
    [athletes, selectedAthleteId],
  );

  const [showActuals, setShowActuals] = useState(initShowActuals);
  // A deep link (?showActuals=true) must not enable the layer for a coach
  // without the permission. Derive the effective value instead of clamping
  // the useState initializer: permissions resolve denied-first while
  // loading, so a clamped initial value would stay false after they load.
  const effectiveShowActuals = showActuals && canEditActuals && canViewActuals;
  const {
    block,
    actualWorkoutLayers,
    athletePublishedWorkoutLayers,
    modifiedPublishedWorkoutLayers,
  } = props;

  const view: BlockView =
    groupBy === 'session'
      ? { mode: 'session', sessionId }
      : { mode: 'cycle', cycleId }; // unused when groupBy === 'all'

  // Clear recent swap tracking when the user changes views or athletes
  useEffect(() => {
    jotaiStore.set(recentSwapsAtom, new Map());
  }, [sessionId, cycleId, groupBy, selectedAthleteId]);

  const actualWorkoutLayersForSelectedProfile = actualWorkoutLayers.filter(
    (aw) => aw.profileId === selectedAthleteId,
  );
  const athletePublishedLayersForSelectedProfile =
    athletePublishedWorkoutLayers.filter(
      (aw) => aw.profileId === selectedAthleteId,
    );
  const modifiedPublishedWorkoutLayersForSelectedProfile =
    modifiedPublishedWorkoutLayers.filter(
      (aw) => aw.profileId === selectedAthleteId,
    );

  return (
    <>
      <Box
        // Full-width bottom border so the calendar and the All-view edit panel
        // sit beneath a single continuous divider (no "open" panel top).
        className="border-b border-gray-200"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {block.focuses.map((focus) => (
            <Box
              key={focus.id}
              sx={{
                alignSelf: 'stretch',
                px: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                backgroundColor: info.lighter,
              }}
            >
              <Typography variant="subtitle1Semibold" component="span">
                {capitalize(focus.name)}
              </Typography>
            </Box>
          ))}
          {!useSingleAthleteMode && (
            <AthleteSelect
              athletes={athletes}
              selectedAthlete={selectedAthletePreview}
              onChange={setSelectedAthleteId}
              hasError={(profileId: string) =>
                athleteTaskMap[profileId] ?? false
              }
            />
          )}
          {useSingleAthleteMode && athletes[0] && (
            <Typography variant="body1" fontWeight="500">
              {athleteDisplayName(athletes[0])}
            </Typography>
          )}
          {selectedAthletePreview && groupBy !== 'all' && (
            <div className="flex items-center gap-2">
              <DisabledWithTooltip
                disabled={!canEditActuals || !canViewActuals}
                reason={
                  !canEditActuals
                    ? t('permissionDisabled.log', { item: t('actuals') })
                    : t('permissionDisabled.view', { item: t('actuals') })
                }
              >
                <Switch
                  size="small"
                  checked={effectiveShowActuals}
                  onChange={(e) => {
                    setShowActuals(e.target.checked);
                  }}
                  inputProps={{
                    'aria-label': effectiveShowActuals
                      ? t('hideActuals')
                      : t('showActuals'),
                  }}
                />
              </DisabledWithTooltip>
              <Typography variant="body2">{t('showActuals')}</Typography>
            </div>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1Semibold" component="span">
            {t('groupBy')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={groupBy}
            onChange={(_, value) => {
              if (value == null) return;
              setGroupBy(value as typeof groupBy);
            }}
            size="small"
            className="min-w-[160px]"
          >
            <ToggleButton
              value="session"
              // Stable accessible name: automation (QA Wolf smoke flows) and
              // assistive tech locate this control by role + name, and the
              // block's label is user-configurable. The visible text below
              // stays the block's dynamic label. (SC-4811 briefly used
              // block.sessionLabel here and broke `getByRole('button', { name: 'session' })`.)
              aria-label="session"
              className="flex-1"
            >
              {block.sessionLabel}
            </ToggleButton>
            <ToggleButton
              value="cycle"
              // See the session toggle: stable accessible name, dynamic visible text.
              aria-label="cycle"
              className="flex-1"
            >
              {block.cycleLabel}
            </ToggleButton>
            <ToggleButton value="all" aria-label={t('all')} className="flex-1">
              {t('all')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {selectedAthletePreview === null ? (
        <BlockSheet
          {...props}
          view={view}
          groupBy={groupBy}
          readOnly={isReadOnly}
          onBlockLayoutChange={onBlockLayoutChange}
          singleAthleteProfileId={
            useSingleAthleteMode ? athletes[0]?.id ?? null : null
          }
          selectedWorkoutId={selectedWorkoutId}
        />
      ) : (
        <AthleteSheet
          {...props}
          view={view}
          groupBy={groupBy}
          readOnly={isReadOnly}
          selectedAthlete={selectedAthletePreview}
          selectedWorkoutId={selectedWorkoutId}
          showActuals={effectiveShowActuals}
          actualWorkoutLayers={actualWorkoutLayersForSelectedProfile}
          athletePublishedWorkoutLayers={
            athletePublishedLayersForSelectedProfile
          }
          modifiedPublishedWorkoutLayers={
            modifiedPublishedWorkoutLayersForSelectedProfile
          }
        />
      )}

      {groupBy !== 'all' && <div className="flex-shrink flex-grow" />}

      {groupBy !== 'all' && (
        <Footer>
          {groupBy === 'session'
            ? block.sortedSessions.map((s) => (
                <FooterButton
                  key={s.id}
                  selected={s.id === sessionId}
                  onClick={() => setSessionId(s.id)}
                >
                  {s.name}
                </FooterButton>
              ))
            : block.sortedCycles.map((c) => (
                <FooterButton
                  key={c.id}
                  selected={c.id === cycleId}
                  onClick={() => setCycleId(c.id)}
                >
                  {c.name}
                </FooterButton>
              ))}
        </Footer>
      )}
    </>
  );
};

// Maybe move into sheets folder?
const Footer = ({ children }: { children: ReactNode }) => {
  return <div className="flex gap-px bg-black pb-2 pl-2">{children}</div>;
};

const FooterButton = ({
  children,
  selected,
  onClick,
}: {
  onClick: () => void;
  children?: ReactNode;
  selected: boolean;
}) => {
  return (
    <Button
      sx={{
        fontWeight: 500,
        backgroundColor: selected ? 'grey.600' : 'grey.800',
        color: selected ? 'common.white' : 'grey.300',
        '&:hover': {
          backgroundColor: 'grey.600',
          color: 'common.white',
        },
        borderRadius: 0,
        '&:first-of-type': {
          borderBottomLeftRadius: '4px',
        },
        '&:last-of-type': {
          borderBottomRightRadius: '4px',
        },
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};
